import { useSelector } from 'react-redux';
import { selectSession } from '@client/model';
import { SessionType } from '@shared';
import { useCallback, useMemo } from 'react';
import { useDateFilter } from './use-date-filter';
import { useAdvisorMetricsApi } from './api/use-advisor-metrics-api';
import { UseAdvisorMetricsResult } from '../types/metrics-type';

export function useAdvisorMetrics(): UseAdvisorMetricsResult {
  const session = useSelector(selectSession);
  const enabled = session.sessionType === SessionType.wizard;
  const { data, isLoading, error } = useAdvisorMetricsApi(enabled);
  const {
    dateFilter,
    setPreset: setDatePreset,
    setCustomDates,
    resetFilter,
    lastMonthDates,
    lastWeekDates,
  } = useDateFilter();

  //Core Fields

  const advisorDetails = useMemo(() => {
    const details = data?.advisorDetails || [];
    return details;
  }, [data]);

  const subscriptionDetails = useMemo(() => {
    const details = data?.subscriptionDetails || [];
    return details;
  }, [data]);

  const usageDetails = useMemo(() => {
    const details = data?.usageMetrics || {};
    return details;
  }, [data]);

  //Advisors

  const totalAdvisors = useMemo(() => {
    const total = advisorDetails.length || 0;
    return total;
  }, [advisorDetails]);

  const weekActiveAdvisors = useMemo(() => {
    const weekActive = advisorDetails.filter(
      advisor => advisor.lastOpen && new Date(advisor.lastOpen) >= lastWeekDates.startDate,
    );
    return weekActive;
  }, [advisorDetails, lastWeekDates]);

  const weekInactiveAdvisors = useMemo(() => {
    const weekInactive = advisorDetails.filter(advisor => !weekActiveAdvisors.includes(advisor));
    return weekInactive;
  }, [advisorDetails, weekActiveAdvisors, lastWeekDates]);

  const monthActiveAdvisors = useMemo(() => {
    const monthActive = advisorDetails.filter(
      advisor => advisor.lastOpen && new Date(advisor.lastOpen) >= lastMonthDates.startDate,
    );
    return monthActive;
  }, [advisorDetails, lastMonthDates]);

  const monthInactiveAdvisors = useMemo(() => {
    const monthInactive = advisorDetails.filter(advisor => !monthActiveAdvisors.includes(advisor));
    return monthInactive;
  }, [advisorDetails, monthActiveAdvisors, lastMonthDates]);

  const newlyRegisteredAdvisors = useMemo(() => {
    return advisorDetails.filter(
      advisor =>
        new Date(advisor.advisorCreatedDateTime) >= lastWeekDates.startDate &&
        new Date(advisor.advisorCreatedDateTime) <= lastWeekDates.endDate,
    );
  }, [advisorDetails, lastWeekDates]);

  const firstOpenAdvisorsCustomDates = useMemo(() => {
    const firstOpen = advisorDetails.filter(
      advisor =>
        advisor.firstOpen &&
        new Date(advisor.firstOpen) >= dateFilter.startDate &&
        new Date(advisor.firstOpen) <= dateFilter.endDate,
    );
    return firstOpen;
  }, [advisorDetails, dateFilter]);

  const lastOpenAdvisorsCustomDates = useMemo(() => {
    const lastOpen = advisorDetails.filter(
      advisor =>
        advisor.lastOpen &&
        new Date(advisor.lastOpen) >= dateFilter.startDate &&
        new Date(advisor.lastOpen) <= dateFilter.endDate,
    );
    return lastOpen;
  }, [advisorDetails, firstOpenAdvisorsCustomDates, dateFilter]);

  //Subscriptions

  const totalActiveSubscriptions = useMemo(() => {
    const active = subscriptionDetails.filter(
      subscription => subscription.subscriptionStatus === 'active',
    );
    return active;
  }, [subscriptionDetails]);

  const totalInactiveSubscriptions = useMemo(() => {
    const inactive = subscriptionDetails.filter(
      subscription => subscription.subscriptionStatus !== 'active',
    );
    return inactive;
  }, [subscriptionDetails]);

  const activeSubscriptionTotalSeats = useMemo(() => {
    const total = totalActiveSubscriptions.reduce((total, subscription) => {
      return total + subscription.totalSeats;
    }, 0);
    return total;
  }, [totalActiveSubscriptions]);

  const totalUtilizedSeats = useMemo(() => {
    const total = totalActiveSubscriptions.reduce((total, subscription) => {
      const utilizedSeatsForThisSubscription = subscription.seats
        ? subscription.seats.reduce((seatTotal, seat) => {
            return seatTotal + (seat.status === 'active' ? 1 : 0);
          }, 0)
        : 0;
      return total + utilizedSeatsForThisSubscription;
    }, 0);
    return total;
  }, [totalActiveSubscriptions]);

  const subscriptionUtilizationRate = useMemo(() => {
    if (activeSubscriptionTotalSeats === 0) return 0;
    const rate = Math.round((totalUtilizedSeats / activeSubscriptionTotalSeats) * 100);
    return rate;
  }, [totalUtilizedSeats, activeSubscriptionTotalSeats]);

  //Cancellations

  const cancellationsTriggeredLast7Days = useMemo(() => {
    const cancellations = subscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(subscription, lastWeekDates.startDate),
    );
    return cancellations;
  }, [subscriptionDetails, lastWeekDates]);

  const cancellationsTriggeredLast30Days = useMemo(() => {
    const cancellations = subscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(subscription, lastMonthDates.startDate),
    );
    return cancellations;
  }, [subscriptionDetails, lastMonthDates]);

  const cancellationsTriggeredWithinCustomDates = useMemo(() => {
    const cancellations = subscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(
        subscription,
        dateFilter.startDate,
        dateFilter.endDate,
      ),
    );
    return cancellations;
  }, [subscriptionDetails, dateFilter]);

  //Actions

  const transformSubscriptionDetailsForDataTable = useCallback(
    (subscriptions: AdvisorSubscriptionMetrics[]) => {
      return subscriptions.map(subscription => {
        const ownerAdvisor = advisorDetails.find(
          advisor => advisor.advisorId === subscription.ownerAdvisorId,
        );
        const ownerAdvisorName = ownerAdvisor
          ? `${ownerAdvisor.advisorFirstName} ${ownerAdvisor.advisorLastName}`
          : 'Unknown';

        const ownerSubscriptionSeat = subscription.seats?.find(
          seat => seat.advisorId === subscription.ownerAdvisorId,
        );

        const cancellationTriggered = ownerSubscriptionSeat?.metadata?.is_cancellation_triggered
          ? 'Yes'
          : 'No';
        const cancellationTriggeredAt = ownerSubscriptionSeat?.metadata?.cancellation_timestamp
          ? new Date(ownerSubscriptionSeat.metadata.cancellation_timestamp).toLocaleDateString()
          : 'N/A';
        const cancellationTriggeredReason =
          ownerSubscriptionSeat?.metadata?.cancellation_reason || 'N/A';
        const additionalFeedback = ownerSubscriptionSeat?.metadata?.cancellation_feedback || 'N/A';

        const transformedSeats = subscription.seats?.map(seat => {
          const seatAdvisor = advisorDetails.find(advisor => advisor.advisorId === seat.advisorId);
          const advisorName = seatAdvisor
            ? `${seatAdvisor.advisorFirstName} ${seatAdvisor.advisorLastName}`
            : 'Unknown';
          const advisorOrganization = seatAdvisor?.organizationName || 'Unknown';
          const isOwner = seat.advisorId === subscription.ownerAdvisorId;

          return {
            ...seat,
            advisorName,
            advisorOrganization,
            isOwner,
          };
        });

        return {
          ...subscription,
          ownerAdvisorName,
          cancellationTriggered,
          cancellationTriggeredAt,
          cancellationTriggeredReason,
          additionalFeedback,
          seats: transformedSeats,
        };
      });
    },
    [advisorDetails],
  );

  const getFilteredAdvisors = useCallback(
    (filterKey?: string | null): AdvisorMetrics[] => {
      if (!filterKey || filterKey === 'all') return advisorDetails;

      switch (filterKey) {
        case 'weeklyActive':
          return weekActiveAdvisors;
        case 'weeklyInactive':
          return weekInactiveAdvisors;
        case 'monthlyActive':
          return monthActiveAdvisors;
        case 'monthlyInactive':
          return monthInactiveAdvisors;
        case 'newlyRegistered':
          return newlyRegisteredAdvisors;
        case 'newInPeriod':
          return firstOpenAdvisorsCustomDates;
        case 'inactiveInPeriod':
          return lastOpenAdvisorsCustomDates;
        default:
          return advisorDetails;
      }
    },
    [
      advisorDetails,
      weekActiveAdvisors,
      weekInactiveAdvisors,
      monthActiveAdvisors,
      monthInactiveAdvisors,
      newlyRegisteredAdvisors,
      firstOpenAdvisorsCustomDates,
      lastOpenAdvisorsCustomDates,
    ],
  );

  const getFilteredSubscriptions = useCallback(
    (filterKey?: string | null) => {
      let subscriptionData = subscriptionDetails;
      if (!filterKey) return transformSubscriptionDetailsForDataTable(subscriptionData);

      switch (filterKey) {
        case 'active':
          subscriptionData = totalActiveSubscriptions;
          break;
        case 'inactive':
          subscriptionData = totalInactiveSubscriptions;
          break;
        case 'cancellationsWeekly':
          subscriptionData = cancellationsTriggeredLast7Days;
          break;
        case 'cancellationsMonthly':
          subscriptionData = cancellationsTriggeredLast30Days;
          break;
        case 'cancellationsInPeriod':
          subscriptionData = cancellationsTriggeredWithinCustomDates;
          break;
        default:
          subscriptionData = subscriptionDetails;
      }
      return transformSubscriptionDetailsForDataTable(subscriptionData);
    },
    [
      subscriptionDetails,
      transformSubscriptionDetailsForDataTable,
      totalActiveSubscriptions,
      totalInactiveSubscriptions,
      cancellationsTriggeredLast7Days,
      cancellationsTriggeredLast30Days,
      cancellationsTriggeredWithinCustomDates,
    ],
  );

  const getFilteredAdvisorsForUsage = useCallback(
    (filterKey?: string | null): AdvisorMetrics[] => {
      if (!filterKey || filterKey === 'all') return advisorDetails;

      switch (filterKey) {
        case 'weeklyActive':
          return weekActiveAdvisors;
        case 'weeklyInactive':
          return weekInactiveAdvisors;
        case 'monthlyActive':
          return monthActiveAdvisors;
        case 'monthlyInactive':
          return monthInactiveAdvisors;
        case 'newlyRegistered':
          return newlyRegisteredAdvisors;
        case 'newInPeriod':
          return firstOpenAdvisorsCustomDates;
        case 'inactiveInPeriod':
          return lastOpenAdvisorsCustomDates;
        case 'active':
        case 'inactive':
        case 'cancellationsWeekly':
        case 'cancellationsMonthly':
        case 'cancellationsInPeriod': {
          // For subscription filters, extract advisor IDs from subscription seats
          const subscriptions = getFilteredSubscriptions(filterKey);
          const advisorIds = new Set<string>();
          subscriptions.forEach(subscription => {
            subscription.seats?.forEach(seat => {
              if (seat.advisorId) {
                advisorIds.add(seat.advisorId);
              }
            });
          });
          return advisorDetails.filter(advisor => advisorIds.has(advisor.advisorId));
        }
        default:
          return advisorDetails;
      }
    },
    [
      advisorDetails,
      weekActiveAdvisors,
      weekInactiveAdvisors,
      monthActiveAdvisors,
      monthInactiveAdvisors,
      newlyRegisteredAdvisors,
      firstOpenAdvisorsCustomDates,
      lastOpenAdvisorsCustomDates,
      getFilteredSubscriptions,
    ],
  );

  return {
    data: {
      advisorMetrics: {
        advisorDetails,
        totalAdvisors,
        weekActiveAdvisors,
        weekInactiveAdvisors,
        monthActiveAdvisors,
        monthInactiveAdvisors,
        newlyRegisteredAdvisors,
        firstOpenAdvisorsCustomDates,
        lastOpenAdvisorsCustomDates,
      },
      subscriptionMetrics: {
        subscriptionDetails,
        totalActiveSubscriptions,
        totalInactiveSubscriptions,
        activeSubscriptionTotalSeats,
        totalUtilizedSeats,
        subscriptionUtilizationRate,
      },
      cancellationMetrics: {
        cancellationsTriggeredLast7Days,
        cancellationsTriggeredLast30Days,
        cancellationsTriggeredWithinCustomDates,
      },
      usageMetrics: {
        usageDetails,
      },
      misc: {
        isLoading,
        error,
        dateFilter,
      },
    },
    actions: {
      setDatePreset,
      setCustomDates,
      resetFilter,
      getFilteredAdvisors,
      getFilteredSubscriptions,
      getFilteredAdvisorsForUsage,
    },
  };
}

function isCancellationTriggeredWithinDateRange(
  subscription: AdvisorSubscriptionMetrics,
  startDate: Date,
  endDate?: Date,
): boolean {
  const ownerAdvisorId = subscription.ownerAdvisorId;
  const ownerSubscriptionSeat = subscription.seats?.find(seat => seat.advisorId === ownerAdvisorId);
  const cancellationTriggeredWithinCustomDates = ownerSubscriptionSeat
    ? ownerSubscriptionSeat.metadata?.is_cancellation_triggered &&
      ownerSubscriptionSeat.metadata?.cancellation_timestamp &&
      new Date(ownerSubscriptionSeat.metadata.cancellation_timestamp) >= startDate &&
      (!endDate || new Date(ownerSubscriptionSeat.metadata.cancellation_timestamp) <= endDate)
    : false;
  return cancellationTriggeredWithinCustomDates;
}
